import { useState, useEffect } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import {
  Facebook,
  Linkedin,
} from 'react-bootstrap-icons';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { ReactComponent as XIcon } from 'Images/public/article-header/XIcon.svg';
import { ReactComponent as EmailIcon } from 'Images/public/article-header/EmailIcon.svg';
import { ReactComponent as PrintIcon } from 'Images/public/article-header/PrintIcon.svg';
import { IconButton, IconTextButton } from 'Components/Button';
import { getDocumentPublicInfo } from 'Utils/utilityFunctions';

const ArticleHeader = ({ article, showShare = true }) => {
  const { type, topics, title, issueNumber, publishDate, lastEditedDate, downloadGroupId } = article;
  const [filename, setFilename] = useState(null);

  useEffect(() => {
    if (downloadGroupId) {
      getDocumentPublicInfo(downloadGroupId).then(res => {
        const info = res.data.documentTagInfo;
        setFilename(`${info.docDisplayName}.${info.docExt}`);
      }).catch(() => {});
    }
  }, [downloadGroupId]);

  const pageUrl = encodeURIComponent(window.location.href);
  const shareTitle = issueNumber ? `Share PSOPPC Article issue ${issueNumber} - ${title}` : `Share PSOPPC Article - ${title}`;
  const encodedShareTitle = encodeURIComponent(shareTitle);

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${pageUrl}&text=${encodedShareTitle}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${pageUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${pageUrl}`,
    email: `mailto:?subject=${encodedShareTitle}&body=${pageUrl}`,
  };

  return (
    <header className="article-header" id="mainContent">
      <Container>
        <Row className="d-flex align-items-center w-100 m-0 p-0 gx-5 gy-4">

          {/* ── LEFT COLUMN ── */}
          <Col xs={12} lg={7} className="text-start">

            {type && (
              <span className="header2 semibold color-white">{type}</span>
            )}

            <h1 className="displayXL bold color-white my-4">{title}</h1>

            <div className="mb-4">
              {(issueNumber || publishDate) && (
                <div className="article-meta-row mb-2">
                  {issueNumber && (
                    <span className="header2 semibold color-white">Issue {issueNumber}</span>
                  )}
                  {issueNumber && publishDate && (
                    <span className="bodyXS bold color-white article-meta-dot">•</span>
                  )}
                  {publishDate && (
                    <span className="header2 regular color-white">{publishDate}</span>
                  )}
                </div>
              )}
              {lastEditedDate && (
                <p className="body color-white mb-0 fst-italic">
                  Last edited: {lastEditedDate}
                </p>
              )}
            </div>

            {topics?.length > 0 && (
              <div className="article-topics-row">
                <SellOutlinedIcon className="article-topic-sell-icon" aria-hidden="true" />
                {topics.map((topic) => (
                  <div key={topic} className="article-topic-chip">
                    <span className="bodyXS semibold">{topic}</span>
                  </div>
                ))}
              </div>
            )}

          </Col>

          {/* ── RIGHT COLUMN ── */}
          {(downloadGroupId || showShare) && (
            <Col xs={12} lg={4} className="d-flex flex-column align-items-start ms-lg-auto">

              {downloadGroupId ? (
                <IconTextButton
                  groupId={downloadGroupId}
                  download={filename}
                  variant="normal"
                  icon={() => <DownloadIcon />}
                  content="Download PDF"
                  className="article-download-btn mb-4 mt-3 mt-lg-0"
                />
              ) : (
                <div className="mb-4" />
              )}

              {showShare && (
                <div className="article-share-section">
                  <p className="header2 bold color-white mb-2">Share:</p>
                  <div className="article-share-icons">
                    <IconButton id="article-share-x" href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" icon={<XIcon />} hiddenText="Share on X (Twitter)" className="article-share-btn" />
                    <IconButton id="article-share-facebook" href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" icon={<Facebook />} hiddenText="Share on Facebook" className="article-share-btn" />
                    <IconButton id="article-share-linkedin" href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" icon={<Linkedin />} hiddenText="Share on LinkedIn" className="article-share-btn" />
                    <IconButton id="article-share-email" href={shareLinks.email} icon={<EmailIcon />} hiddenText="Share via email" className="article-share-btn" />
                    <IconButton id="article-share-print" onClick={() => window.print()} icon={<PrintIcon />} hiddenText="Print this article" className="article-share-btn" />
                  </div>
                </div>
              )}

            </Col>
          )}

        </Row>
      </Container>
    </header>
  );
};

export default ArticleHeader;
